import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import AnalyzingIndicator from './AnalyzingIndicator';

function AdminUsageData({ adminToken, tabName }) {
  const isProduction = process.env.NODE_ENV === 'production';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, all
  const [sidebarVisible, setSidebarVisible] = useState(true); // Sidebar toggle state
  const [activeSection, setActiveSection] = useState('overview');

  // Key Metrics State
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    trialUsers: 0,
    paidUsers: 0,
    totalGatherings: 0,
    totalBookings: 0,
    conversionRate: 0,
    churnRate: 0,
    avgBookingsPerUser: 0,
    trialToConversionRate: 0,
    mrr: 0, // Monthly Recurring Revenue
    ltv: 0, // Lifetime Value
  });

  // Stripe-specific metrics
  const [stripeMetrics, setStripeMetrics] = useState({
    totalRevenue: 0,
    activeSubscriptions: 0,
    canceledSubscriptions: 0,
    trialingSubscriptions: 0,
    avgRevenuePerUser: 0,
    paymentSuccessRate: 0,
    topPayingCustomers: [],
  });

  // Time-based data
  const [userGrowth, setUserGrowth] = useState([]);
  const [bookingTrends, setBookingTrends] = useState([]);

  // Cohort retention (see AdminRepository.get_cohort_retention) - real
  // month-by-month retention per signup cohort, not example data.
  const [cohortData, setCohortData] = useState([]);

  // Demographics
  const [demographics, setDemographics] = useState({
    byAge: [],
    byLocation: [],
    byGender: [],
  });

  // Theme Analytics
  const [themeStats, setThemeStats] = useState([]);

  // Booking Patterns
  const [bookingPatterns, setBookingPatterns] = useState({
    byDayOfWeek: [],
    byTimeOfDay: {},
    byTheme: [],
  });

  // AI review of this tab's aggregate analytics (see usage_analytics_ai.py)
  // - same "run on demand, keep the latest, show/hide" pattern as
  // BlogAdmin.js's Content Coverage Review. Only ever sent already-
  // anonymous, aggregate data (totals, group-by breakdowns) - never
  // individual user rows, and never Stripe's topPayingCustomers (real
  // emails) or cohort retention (still placeholder numbers server-side).
  const [usageReview, setUsageReview] = useState(null);
  const [usageReviewing, setUsageReviewing] = useState(false);
  const [usageReviewError, setUsageReviewError] = useState('');
  const [showUsageReview, setShowUsageReview] = useState(false);

  useEffect(() => {
    if (tabName === 'UsageData') {
      loadAllData();
    }
  }, [tabName, timeRange]);

  // Handle responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarVisible(false); // Hide sidebar on smaller screens
      } else {
        setSidebarVisible(true); // Show sidebar on larger screens
      }
    };

    // Set initial state based on screen size
    handleResize();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to section smoothly
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);

    // Close sidebar on mobile first to avoid layout shift during scroll
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      setSidebarVisible(false);
    }

    // Wait for sidebar animation to complete before scrolling
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Add offset for better visibility
        setTimeout(() => {
          window.scrollBy({ top: -80, behavior: 'smooth' });
        }, 100);
      }
    }, isMobile ? 350 : 0); // Wait for sidebar transition (0.3s) + buffer
  };

  // Navigation sections configuration
  const navSections = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'ai-review', label: 'AI Review', icon: '🤖' },
    { id: 'revenue', label: 'Revenue & Subscriptions', icon: '💰' },
    { id: 'funnel', label: 'Conversion Funnel', icon: '🎯' },
    { id: 'themes', label: 'Theme Performance', icon: '🎨' },
    { id: 'patterns', label: 'Booking Patterns', icon: '📊' },
    { id: 'demographics', label: 'Demographics', icon: '👥' },
    { id: 'lifecycle', label: 'Subscription Lifecycle', icon: '🔄' },
    { id: 'cohort', label: 'Cohort Retention', icon: '📈' },
    { id: 'roadmap', label: 'Implementation Roadmap', icon: '🚀' },
  ];

  const loadAllData = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch all analytics data from backend APIs in parallel
      const [
        usageData,
        stripeData,
        lifecycleData,
        demographicsData,
        themePerformanceData,
        bookingPatternsData,
        cohortRetentionData
      ] = await Promise.all([
        fetch('/api/admin/analytics/usage', {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }).then(res => res.json()),

        fetch('/api/admin/analytics/stripe-metrics', {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }).then(res => res.json()),

        fetch('/api/admin/analytics/subscription-lifecycle', {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }).then(res => res.json()),

        fetch('/api/admin/analytics/demographics', {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }).then(res => res.json()),

        fetch('/api/admin/analytics/theme-performance', {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }).then(res => res.json()),

        fetch('/api/admin/analytics/booking-patterns', {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }).then(res => res.json()),

        fetch('/api/admin/analytics/cohort-retention', {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }).then(res => res.json())
      ]);

      // Set usage metrics
      setMetrics({
        totalUsers: usageData.totalUsers || 0,
        activeUsers: usageData.activeUsers || 0,
        trialUsers: stripeData.trialingSubscriptions || 0,
        paidUsers: stripeData.activeSubscriptions || 0,
        totalGatherings: usageData.totalGatherings || 0,
        totalBookings: usageData.totalBookings || 0,
        conversionRate: stripeData.activeSubscriptions && usageData.totalUsers
          ? ((stripeData.activeSubscriptions / usageData.totalUsers) * 100).toFixed(1)
          : 0,
        churnRate: stripeData.canceledSubscriptions && (stripeData.activeSubscriptions + stripeData.canceledSubscriptions)
          ? ((stripeData.canceledSubscriptions / (stripeData.activeSubscriptions + stripeData.canceledSubscriptions)) * 100).toFixed(1)
          : 0,
        avgBookingsPerUser: usageData.avgBookingsPerUser || 0,
        trialToConversionRate: lifecycleData.trialConversionRate || 0,
        mrr: (stripeData.mrr || 0).toFixed(2), // Net of Stripe's per-transaction fee - see get_stripe_metrics
        ltv: ((stripeData.totalRevenue || 0) / ((stripeData.activeSubscriptions + stripeData.canceledSubscriptions) || 1)).toFixed(2)
      });

      // Set Stripe metrics
      setStripeMetrics({
        totalRevenue: stripeData.totalRevenue || 0,
        activeSubscriptions: stripeData.activeSubscriptions || 0,
        canceledSubscriptions: stripeData.canceledSubscriptions || 0,
        trialingSubscriptions: stripeData.trialingSubscriptions || 0,
        avgRevenuePerUser: stripeData.avgRevenuePerUser || 0,
        paymentSuccessRate: stripeData.paymentSuccessRate || 0,
        topPayingCustomers: Array.isArray(stripeData.topPayingCustomers) ? stripeData.topPayingCustomers : [],
      });

      // Set demographics
      setDemographics({
        byAge: Object.entries(demographicsData.ageGroups || {}).map(([age, count]) => ({
          age,
          count
        })),
        byLocation: Array.isArray(demographicsData.locations) ? demographicsData.locations : [],
        byGender: Array.isArray(demographicsData.genders) ? demographicsData.genders : [],
      });

      // Set theme performance
      setThemeStats(Array.isArray(themePerformanceData) ? themePerformanceData.map(theme => ({
        name: theme.themeName,
        bookings: theme.bookingCount,
        gatherings: theme.gatheringCount
      })) : []);

      // Set booking patterns
      const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const bookingsByDay = Object.entries(bookingPatternsData.byDayOfWeek || {})
        .map(([day, count]) => ({ day, count }))
        .sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));

      setBookingPatterns({
        byDayOfWeek: bookingsByDay,
        byTimeOfDay: bookingPatternsData.byTimeOfDay || {},
        byTheme: Array.isArray(themePerformanceData) ? themePerformanceData.map(t => ({
          theme: t.themeName,
          count: t.bookingCount
        })) : []
      });

      // Set user growth from booking trends
      setUserGrowth(Array.isArray(bookingPatternsData.byMonth) ? bookingPatternsData.byMonth : []);
      setBookingTrends(Array.isArray(bookingPatternsData.byMonth) ? bookingPatternsData.byMonth : []);

      setCohortData(Array.isArray(cohortRetentionData) ? cohortRetentionData : []);

      setLoading(false);
    } catch (err) {
      console.error('Failed to load usage data:', err);
      setError('Failed to load analytics data. Some features may require additional backend support.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch('/api/admin/analytics/usage-review', { headers: { 'Authorization': `Bearer ${adminToken}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setUsageReview)
      .catch(() => setUsageReview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const handleRunUsageReview = async () => {
    setUsageReviewing(true);
    setUsageReviewError('');
    try {
      const response = await fetch('/api/admin/analytics/usage-review', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Failed to run review');
      setUsageReview(await response.json());
      setShowUsageReview(true);
    } catch (err) {
      setUsageReviewError(err.message);
    } finally {
      setUsageReviewing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          display: 'inline-block',
          width: '40px',
          height: '40px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #8B5FBF',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <p style={{ marginTop: '20px', color: '#666' }}>Loading analytics...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', position: 'relative', minHeight: '100vh' }}>
      <style>{`
        @media (max-width: 768px) {
          .usage-data-section {
            padding: 0.25rem !important;
            margin-bottom: 0.5rem !important;
          }
          .usage-data-section h2,
          .usage-data-section h3 {
            font-size: 1rem !important;
            margin-bottom: 0.25rem !important;
          }
          .usage-data-section p {
            font-size: 0.7rem !important;
            margin-bottom: 0.25rem !important;
          }
          .metric-card {
            padding: 0.4rem !important;
            margin: 0.25rem !important;
          }
          .metric-card h4 {
            font-size: 0.7rem !important;
            margin-bottom: 0.2rem !important;
          }
          .metric-card .value {
            font-size: 1rem !important;
          }
          /* Ensure toggle button is always visible on mobile */
          button[style*="z-index: 1001"] {
            box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
          }
          /* Make metric grid more compact on mobile */
          #overview {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.4rem !important;
            margin-bottom: 0.75rem !important;
          }
          /* Reduce header padding on mobile */
          .usage-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            margin-bottom: 0.75rem !important;
            gap: 0.5rem !important;
          }
          .usage-header h2 {
            font-size: 1.2rem !important;
          }
          .usage-header select {
            padding: 0.4rem 0.6rem !important;
            font-size: 0.8rem !important;
          }
          .usage-header label {
            font-size: 0.75rem !important;
          }
          /* Make all grids 2-column on mobile */
          div[style*="gridTemplateColumns"] {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.4rem !important;
          }
          /* Reduce section card padding */
          .section-card {
            padding: 0.5rem !important;
            margin-bottom: 0.75rem !important;
          }
          /* Make tables more compact */
          table {
            font-size: 0.7rem !important;
          }
          table th,
          table td {
            padding: 0.3rem !important;
            font-size: 0.7rem !important;
          }
          /* Reduce error message padding */
          div[style*="backgroundColor: #fff3cd"] {
            padding: 0.5rem !important;
            margin-bottom: 0.75rem !important;
            font-size: 0.75rem !important;
          }
        }
      `}</style>
      {/* Overlay for mobile when sidebar is open */}
      {sidebarVisible && window.innerWidth < 1024 && (
        <div
          onClick={() => setSidebarVisible(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
        />
      )}

      {/* Sidebar Navigation */}
      <div style={{
        position: 'fixed',
        left: sidebarVisible ? '0' : (window.innerWidth < 768 ? '-250px' : '-280px'),
        top: '0',
        width: window.innerWidth < 768 ? '250px' : '280px',
        height: '100vh',
        backgroundColor: '#fff',
        borderRight: '2px solid #e0e0e0',
        overflowY: 'auto',
        transition: 'left 0.3s ease',
        zIndex: 1000,
        boxShadow: sidebarVisible ? '2px 0 10px rgba(0,0,0,0.1)' : 'none',
      }}>
        <div style={{
          padding: '20px',
          borderBottom: '2px solid #667eea',
          background: 'linear-gradient(135deg, #8B9FFF 0%, #A78BFA 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Navigation</h3>
        </div>

        <nav style={{ padding: '10px 0' }}>
          {navSections.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '12px 20px',
                border: 'none',
                backgroundColor: activeSection === section.id ? '#f0f0ff' : 'transparent',
                borderLeft: activeSection === section.id ? '4px solid #667eea' : '4px solid transparent',
                color: activeSection === section.id ? '#667eea' : '#333',
                fontSize: '0.95rem',
                fontWeight: activeSection === section.id ? '600' : '400',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                if (activeSection !== section.id) {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== section.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>{section.icon}</span>
              <span>{section.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setSidebarVisible(!sidebarVisible)}
        style={{
          position: 'fixed',
          left: sidebarVisible ? (window.innerWidth < 768 ? '250px' : '280px') : '0',
          top: '20px',
          width: window.innerWidth < 768 ? '35px' : '40px',
          height: window.innerWidth < 768 ? '35px' : '40px',
          backgroundColor: '#667eea',
          color: '#fff',
          border: 'none',
          borderRadius: '0 8px 8px 0',
          cursor: 'pointer',
          zIndex: 1001,
          transition: 'left 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: window.innerWidth < 768 ? '1rem' : '1.2rem',
          boxShadow: '2px 2px 8px rgba(0,0,0,0.2)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#764ba2';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#667eea';
        }}
        title={sidebarVisible ? 'Hide Navigation' : 'Show Navigation'}
      >
        {sidebarVisible ? '◀' : '▶'}
      </button>

      {/* Main Content */}
      <div style={{
        flex: 1,
        marginLeft: window.innerWidth < 1024 ? '0' : (sidebarVisible ? '280px' : '0'),
        transition: 'margin-left 0.3s ease',
        padding: window.innerWidth < 768 ? '8px' : '20px',
        maxWidth: '100%',
        overflowX: 'hidden',
      }}>
        <div className="usage-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2 style={{ margin: 0 }}>Usage Data & Analytics</h2>

          {/* Time Range Selector */}
          <div>
            <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Time Range:</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                cursor: 'pointer',
              }}
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>

      {error && (
        <div style={{
          padding: '15px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #ffeaa7',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Key Metrics Grid */}
      <div id="overview" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <MetricCard title="Total Users" value={metrics.totalUsers} icon="👥" color="#667eea" />
        <MetricCard title="Active Users" value={metrics.activeUsers} icon="✅" color="#38ef7d" />
        <MetricCard title="Trial Users" value={metrics.trialUsers} icon="🔄" color="#f7b733" />
        <MetricCard title="Paid Users" value={metrics.paidUsers} icon="💎" color="#764ba2" />
        <MetricCard title="Total Bookings" value={metrics.totalBookings} icon="📅" color="#fc4a1a" />
        <MetricCard title="Avg Bookings/User" value={metrics.avgBookingsPerUser} icon="📊" color="#11998e" />
        <MetricCard title="Conversion Rate" value={`${metrics.conversionRate}%`} icon="🎯" color="#8B5FBF" />
        <MetricCard title="Total Gatherings" value={metrics.totalGatherings} icon="🥳" color="#5ab963" />
      </div>

      <div id="ai-review">
        <SectionCard title="🤖 AI Review">
          <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                {usageReview
                  ? `Last reviewed: ${new Date(usageReview.created_at).toLocaleString()}`
                  : 'Not reviewed yet.'}
              </p>
              <button
                type="button"
                onClick={handleRunUsageReview}
                disabled={usageReviewing}
                style={{
                  padding: '10px 20px', backgroundColor: usageReviewing ? '#ccc' : '#8B5FBF', color: 'white',
                  border: 'none', borderRadius: '4px', cursor: usageReviewing ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: 'bold',
                }}
              >
                {usageReviewing ? 'Analyzing...' : 'Analyze with Claude'}
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#999', margin: '8px 0 0 0' }}>
              Only sends already-anonymous, aggregate data (usage totals, demographics breakdown, theme performance,
              booking patterns, cohort retention) - never individual user records, and never Stripe's
              top-paying-customers list.
            </p>

            {usageReviewing && <AnalyzingIndicator color="#8B5FBF" />}

            {usageReviewError && (
              <div style={{
                marginTop: '15px', color: 'red', padding: '10px',
                border: '1px solid red', borderRadius: '4px', backgroundColor: '#ffe6e6', fontSize: '13px'
              }}>
                {usageReviewError}
              </div>
            )}

            {usageReview && (
              <div style={{ marginTop: '15px' }}>
                <button
                  type="button"
                  onClick={() => setShowUsageReview((v) => !v)}
                  style={{
                    padding: '5px 15px', backgroundColor: '#6c757d', color: 'white',
                    border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px'
                  }}
                >
                  {showUsageReview ? 'Hide review' : 'Show review'}
                </button>

                {showUsageReview && (
                  <div style={{ marginTop: '15px' }}>
                    <p style={{ fontSize: '14px', color: '#333' }}>{usageReview.review_data.overall_summary}</p>

                    {usageReview.review_data.findings?.length > 0 && (
                      <div style={{ marginBottom: '15px' }}>
                        <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Findings</p>
                        {usageReview.review_data.findings.map((f, i) => (
                          <div key={i} style={{ borderTop: '1px solid #eee', padding: '8px 0' }}>
                            <p style={{ margin: '0 0 3px 0', fontSize: '13px' }}><strong>[{f.severity}]</strong> {f.label}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{f.detail}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {usageReview.review_data.recommendations?.length > 0 && (
                      <div>
                        <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Recommendations</p>
                        {usageReview.review_data.recommendations.map((r, i) => (
                          <div key={i} style={{ borderTop: '1px solid #eee', padding: '8px 0' }}>
                            <p style={{ margin: '0 0 3px 0', fontSize: '13px' }}><strong>[{r.priority}]</strong> {r.action}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{r.rationale}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Stripe Revenue Metrics */}
      <div id="revenue">
        <SectionCard title="💰 Revenue & Subscription Metrics (Stripe)">
          <div style={{ padding: '1rem' }}>
          <p style={{ marginTop: 0, marginBottom: '1rem', fontSize: '0.85rem', color: '#666' }}>
            Revenue figures below are net of an estimated £0.30 Stripe processing fee per
            subscription charge — what the business actually keeps, not the gross billed amount.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem',
            marginBottom: '1.5rem',
          }}>
            <RevenueMetric label="Total Revenue (Net)" value={`£${stripeMetrics.totalRevenue.toLocaleString()}`} />
            <RevenueMetric label="MRR (Net)" value={`£${metrics.mrr}`} />
            <RevenueMetric label="Avg LTV (Net)" value={`£${metrics.ltv}`} />
            <RevenueMetric label="Active Subs" value={stripeMetrics.activeSubscriptions} />
            <RevenueMetric label="Trialing" value={stripeMetrics.trialingSubscriptions} />
            <RevenueMetric label="Churned" value={stripeMetrics.canceledSubscriptions} isNegative />
          </div>

          <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Top Paying Customers</h4>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.85rem',
              minWidth: '500px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.85rem' }}>Email</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.85rem' }}>Net Revenue</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.85rem' }}>Bookings</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.85rem' }}>Months Active</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.85rem' }}>Avg/Month</th>
                </tr>
              </thead>
              <tbody>
                {stripeMetrics.topPayingCustomers.map((customer, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                    <td style={{ padding: '0.5rem', fontSize: '0.85rem' }}>{customer.email}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 'bold', color: '#38ef7d', fontSize: '0.85rem' }}>
                      £{customer.revenue}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.85rem' }}>{customer.bookings}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.85rem' }}>{customer.monthsActive}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.85rem' }}>
                      £{(customer.revenue / customer.monthsActive).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          </div>
        </SectionCard>
      </div>

      {/* Conversion Funnel Analysis */}
      <div id="funnel">
        <SectionCard title="🎯 Conversion Funnel">
          <div style={{ padding: '1rem' }}>
          <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem' }}>User Journey</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <FunnelStep label="Total Signups" value={metrics.totalUsers} percentage={100} />
            <FunnelStep
              label="Started Trial"
              value={metrics.trialUsers + metrics.paidUsers}
              percentage={metrics.totalUsers ? ((metrics.trialUsers + metrics.paidUsers) / metrics.totalUsers) * 100 : 0}
            />
            <FunnelStep
              label="Made a Booking"
              value={metrics.activeUsers}
              percentage={metrics.totalUsers ? (metrics.activeUsers / metrics.totalUsers) * 100 : 0}
            />
            <FunnelStep label="Converted to Paid" value={metrics.paidUsers} percentage={metrics.conversionRate} />
          </div>
          </div>
        </SectionCard>
      </div>

      {/* Theme Performance */}
      <div id="themes">
        <SectionCard title="🎨 Theme Performance">
          <div style={{ padding: '1rem' }}>
          <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem' }}>Bookings by Theme</h4>
          {themeStats.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {themeStats.map((theme, index) => {
                const maxBookings = themeStats[0]?.bookings || 1;
                const percentage = (theme.bookings / maxBookings) * 100;

                return (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ minWidth: '150px', fontWeight: '600' }}>{theme.name}</div>
                    <div style={{ flex: 1, backgroundColor: '#f0f0f0', borderRadius: '10px', height: '30px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        backgroundColor: '#8B5FBF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: '10px',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        transition: 'width 0.3s ease',
                      }}>
                        {theme.bookings}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: '#666' }}>No booking data available yet.</p>
          )}
          </div>
        </SectionCard>
      </div>

      {/* Booking Patterns */}
      <div id="patterns">
        <SectionCard title="📊 Booking Patterns">
          <div style={{ padding: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            <div>
              <h4 style={{ marginBottom: '15px' }}>By Day of Week</h4>
              {bookingPatterns.byDayOfWeek.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {bookingPatterns.byDayOfWeek.map((dayData) => {
                    const maxCount = Math.max(...bookingPatterns.byDayOfWeek.map(d => d.count), 1);
                    return (
                      <div key={dayData.day} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ minWidth: '100px', fontSize: '0.9rem' }}>{dayData.day}</div>
                        <div style={{ flex: 1, height: '20px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${(dayData.count / maxCount) * 100}%`,
                            height: '100%',
                            backgroundColor: '#667eea',
                          }} />
                        </div>
                        <div style={{ minWidth: '30px', fontSize: '0.85rem', fontWeight: 'bold' }}>{dayData.count}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#666' }}>No booking pattern data available yet.</p>
              )}
            </div>

            <div>
              <h4 style={{ marginBottom: '15px' }}>Peak Booking Times</h4>
              {Object.values(bookingPatterns.byTimeOfDay).some((v) => v > 0) ? (
                <>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    <li style={listItemStyle}>🌅 Morning (6am-12pm): <strong>{bookingPatterns.byTimeOfDay.Morning || 0}%</strong></li>
                    <li style={listItemStyle}>☀️ Afternoon (12pm-6pm): <strong>{bookingPatterns.byTimeOfDay.Afternoon || 0}%</strong></li>
                    <li style={listItemStyle}>🌆 Evening (6pm-12am): <strong>{bookingPatterns.byTimeOfDay.Evening || 0}%</strong></li>
                    <li style={listItemStyle}>🌙 Night (12am-6am): <strong>{bookingPatterns.byTimeOfDay.Night || 0}%</strong></li>
                  </ul>

                  {bookingPatterns.byDayOfWeek.length > 0 && (() => {
                    const busiestDay = bookingPatterns.byDayOfWeek.reduce(
                      (best, d) => (d.count > best.count ? d : best), bookingPatterns.byDayOfWeek[0]
                    );
                    const busiestTime = Object.entries(bookingPatterns.byTimeOfDay).reduce(
                      (best, [label, pct]) => (pct > best[1] ? [label, pct] : best), ['', -1]
                    );
                    return (
                      <div style={{
                        marginTop: '20px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '0.9rem',
                      }}>
                        <strong>📌 Busiest:</strong> {busiestDay.day} in the {busiestTime[0].toLowerCase()} ({busiestDay.count} bookings on that day of week, {busiestTime[1]}% of bookings fall in that time window).
                      </div>
                    );
                  })()}
                </>
              ) : (
                <p style={{ color: '#666' }}>No booking pattern data available yet.</p>
              )}
            </div>
          </div>
          </div>
        </SectionCard>
      </div>

      <div id="demographics">
          <SectionCard title="👥 User Demographics">
            <div style={{ padding: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <h4>Age Distribution</h4>
                {demographics.byAge.length > 0 ? (
                  <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.9rem' }}>
                    {(() => {
                      const totalAge = demographics.byAge.reduce((sum, a) => sum + a.count, 0) || 1;
                      const maxAge = Math.max(...demographics.byAge.map((a) => a.count));
                      return demographics.byAge.map((a) => (
                        <li key={a.age} style={listItemStyle}>
                          {a.age}: <strong>{Math.round((a.count / totalAge) * 100)}%</strong>{a.count === maxAge ? ' 🔥' : ''}
                        </li>
                      ));
                    })()}
                  </ul>
                ) : (
                  <p style={{ color: '#666', fontSize: '0.9rem' }}>No age data available yet.</p>
                )}
              </div>

              <div>
                <h4>Top Locations</h4>
                {demographics.byLocation.length > 0 ? (
                  <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.9rem' }}>
                    {(() => {
                      const totalLocation = demographics.byLocation.reduce((sum, l) => sum + l.count, 0) || 1;
                      const topLocations = [...demographics.byLocation].sort((a, b) => b.count - a.count).slice(0, 4);
                      return topLocations.map((l) => (
                        <li key={l.city} style={listItemStyle}>
                          {l.city}: <strong>{Math.round((l.count / totalLocation) * 100)}%</strong>{l === topLocations[0] ? ' 🔥' : ''}
                        </li>
                      ));
                    })()}
                  </ul>
                ) : (
                  <p style={{ color: '#666', fontSize: '0.9rem' }}>No location data available yet.</p>
                )}
              </div>

              <div>
                <h4>Gender Split</h4>
                {demographics.byGender.length > 0 ? (
                  <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.9rem' }}>
                    {(() => {
                      const totalGender = demographics.byGender.reduce((sum, g) => sum + g.count, 0) || 1;
                      return demographics.byGender.map((g) => (
                        <li key={g.name} style={listItemStyle}>
                          {g.name}: <strong>{Math.round((g.count / totalGender) * 100)}%</strong>
                        </li>
                      ));
                    })()}
                  </ul>
                ) : (
                  <p style={{ color: '#666', fontSize: '0.9rem' }}>No gender data available yet.</p>
                )}
              </div>
            </div>
            </div>
          </SectionCard>
      </div>

      {/* Subscription Lifecycle Analysis */}
      <div id="lifecycle">
        <SectionCard title="🔄 Subscription Lifecycle & Payment Analytics">
          <div style={{ padding: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <h4>Trial to Paid Conversion</h4>
              <div style={{
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#8B5FBF' }}>{metrics.trialToConversionRate}%</div>
                <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
                  of trials convert to paid
                </div>
                <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '10px' }}>
                  Industry avg: 25-40%
                </div>
              </div>
            </div>

            <div>
              <h4>Payment Success Rate</h4>
              <div style={{
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#38ef7d' }}>
                  {stripeMetrics.paymentSuccessRate}%
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
                  successful payments
                </div>
                <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '10px' }}>
                  Industry avg: 85-90%
                </div>
              </div>
            </div>
          </div>

          <p style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
            📝 Cancellation reasons aren't shown here - Stripe doesn't capture why a subscriber cancels unless the
            checkout/cancellation flow explicitly asks and records it, which this app doesn't do yet. Add a reason
            field to the cancellation flow if that breakdown is wanted.
          </p>
          </div>
        </SectionCard>
      </div>

      <div id="cohort">
        <SectionCard title="📈 Cohort Retention Analysis">
          <div style={{ padding: '1rem' }}>
          <p style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.85rem', color: '#666' }}>
            Of users who signed up in a given month, what % had at least one non-dropped-out booking to a gathering
            happening in each of the following months. Month 0 is always 100% by definition (the signup month
            itself) - Month 1 onward are real retention figures.
          </p>
          {cohortData.length > 0 ? (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.8rem',
                minWidth: '500px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '0.8rem' }}>Cohort</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '0.8rem' }}>Size</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '0.8rem' }}>Month 0</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '0.8rem' }}>Month 1</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '0.8rem' }}>Month 2</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '0.8rem' }}>Month 3</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '0.8rem' }}>Month 4</th>
                  </tr>
                </thead>
                <tbody>
                  {cohortData.map((c) => (
                    <CohortRow
                      key={c.cohort}
                      cohort={c.cohort}
                      size={c.cohort_size}
                      values={[c.month0, c.month1, c.month2, c.month3, c.month4]}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#666' }}>No signup cohorts yet.</p>
          )}
          </div>
        </SectionCard>
      </div>

      {/* Implementation Roadmap - Hide in production (only needed for dev) */}
      {!isProduction && (
        <div id="roadmap">
        <SectionCard title="🚀 Analytics Implementation Roadmap">
          <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '20px' }}>Remaining gaps in real analytics coverage:</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <ImplementationItem
              status="needed"
              title="Cancellation Reason Tracking"
              description="Stripe doesn't capture why a subscriber cancels - add a reason field to the cancellation flow to get a real breakdown"
              priority="Medium"
            />

            <ImplementationItem
              status="needed"
              title="Converted vs. Churned Behavior Comparison"
              description="Needs a defined profile-completeness formula and a real Stripe-customer-to-user join - removed rather than left as fabricated example numbers"
              priority="Medium"
            />

            <ImplementationItem
              status="partial"
              title="Real-time Dashboards"
              description="Live metrics updates using WebSocket or polling"
              priority="Low"
            />
          </div>
          </div>
        </SectionCard>
      </div>
      )}
      </div>
    </div>
  );
}

// Helper Components
const MetricCard = ({ title, value, icon, color }) => (
  <div style={{
    padding: '0.75rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: `2px solid ${color}20`,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
  }}
  >
    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{icon}</div>
    <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>{title}</div>
    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color }}>{value}</div>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div style={{
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '30px',
    overflow: 'hidden',
  }}>
    <div style={{
      padding: '20px',
      background: 'linear-gradient(135deg, #8B9FFF 0%, #A78BFA 100%)',
      color: '#fff',
    }}>
      <h3 style={{ margin: 0, fontSize: '1.3rem' }}>{title}</h3>
    </div>
    {children}
  </div>
);

const FunnelStep = ({ label, value, percentage, isPlaceholder }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
    <div style={{ minWidth: '180px', fontWeight: '600' }}>{label}</div>
    <div style={{ flex: 1, height: '40px', backgroundColor: '#e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{
        width: `${percentage}%`,
        height: '100%',
        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 15px',
        color: 'white',
        fontWeight: 'bold',
        transition: 'width 0.5s ease',
      }}>
        <span>{value}</span>
        <span>{percentage}%</span>
      </div>
    </div>
    {isPlaceholder && <span style={{ fontSize: '0.8rem', color: '#999' }}>*estimated</span>}
  </div>
);

const ImplementationItem = ({ status, title, description, priority }) => {
  const statusConfig = {
    needed: { color: '#dc3545', icon: '❌', label: 'Needed' },
    partial: { color: '#f7b733', icon: '⚠️', label: 'Partial' },
    complete: { color: '#38ef7d', icon: '✅', label: 'Complete' },
  };

  const config = statusConfig[status];

  return (
    <div style={{
      display: 'flex',
      gap: '15px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      borderLeft: `4px solid ${config.color}`,
    }}>
      <div style={{ fontSize: '1.5rem' }}>{config.icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
          <h4 style={{ margin: 0 }}>{title}</h4>
          <span style={{
            padding: '2px 8px',
            backgroundColor: config.color,
            color: 'white',
            borderRadius: '8px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
          }}>
            {priority}
          </span>
        </div>
        <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#666' }}>{description}</p>
      </div>
    </div>
  );
};

const listItemStyle = {
  padding: '8px 0',
  borderBottom: '1px solid #f0f0f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const RevenueMetric = ({ label, value, trend, isNegative }) => (
  <div style={{
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #e9ecef',
  }}>
    <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>{label}</div>
    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{value}</div>
    <div style={{
      fontSize: '0.75rem',
      color: isNegative ? '#dc3545' : '#38ef7d',
      fontWeight: 'bold',
    }}>
      {trend}
    </div>
  </div>
);

const CohortRow = ({ cohort, size, values }) => (
  <tr style={{ borderBottom: '1px solid #e9ecef' }}>
    <td style={{ padding: '0.5rem', fontWeight: 'bold', fontSize: '0.8rem' }}>{cohort}</td>
    <td style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.8rem' }}>{size}</td>
    {values.map((value, index) => {
      let bgColor = '#f8f9fa';
      if (value >= 80) bgColor = '#d4edda';
      else if (value >= 60) bgColor = '#fff3cd';
      else if (value >= 40) bgColor = '#f8d7da';
      else bgColor = '#e9ecef';

      return (
        <td key={index} style={{
          padding: '0.5rem',
          textAlign: 'center',
          backgroundColor: bgColor,
          fontWeight: index === 0 ? 'bold' : 'normal',
          fontSize: '0.8rem'
        }}>
          {value}%
        </td>
      );
    })}
  </tr>
);

export default AdminUsageData;
